import React, { useEffect, lazy, Suspense } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { UserProvider } from './context/UserContext';
import { WishlistProvider } from './context/WishlistContext';
import NotificationProvider from './components/NotificationSystem/NotificationSystem';
import HomePage from './pages/HomePage/HomePage';
import Header from './components/Header/Header';
import AdminSessionGate from './components/AdminSessionGate/AdminSessionGate';
import { MessagesProvider } from './components/Messages/MessagesContext';
import MessagesDrawer from './components/Messages/MessagesDrawer';
import { NotificationsProvider } from './components/Notifications/NotificationsContext';
import './App.css';
import './components/SharedCss/mobileSafeArea.css';
import AdMobBannerManager from './components/AdMobBannerManager/AdMobBannerManager';
import PageLoader from './components/PageLoader/PageLoader';
import { isAndroidCapacitor, isAndroidTablet } from './utils/platformUtils';
import { scheduleIdlePrefetch } from './utils/prefetchRoute';
import { syncBrowseSessionToPathname } from './utils/browseSessionStorage';

import LoginPage from './pages/Login/Login';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import SearchCollectibles from './pages/SearchCollectibles/SearchCollectibles';
import Marketplace from './pages/Marketplace/Marketplace';
import ViewCollectionItem from './pages/ViewCollectionItem/ViewCollectionItem';
import ViewMarketplaceItem from './pages/ViewMarketplaceItem/ViewMarketplaceItem';
import CreateMarketplaceItem from './pages/CreateMarketplaceItem/CreateMarketplaceItem';
import MyMarketplaceListings from './pages/MyMarketplaceListings/MyMarketplaceListings';
import SearchHeroCards from './pages/SearchHeroCards/SearchHeroCards';

const SignupPage = lazy(() => import('./pages/SignUp/SignUp'));
const SubmitResetPassword = lazy(() => import('./pages/SubmitResetPassword/SubmitResetPassword'));
const NewPasswordReset = lazy(() => import('./pages/NewPasswordReset/NewPasswordReset'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail/VerifyEmail'));
const ResendEmailVerification = lazy(() => import('./pages/ResendEmailVerification/ResendEmailVerification'));
const AddCollectible = lazy(() => import('./pages/AddCollectible/AddCollectible'));
const MyItems = lazy(() => import('./pages/MyItems/MyItems'));
const SearchUsersItems = lazy(() => import('./pages/SearchUsersCollection/SearchUsersItems'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const BlogHomePage = lazy(() => import('./pages/BlogHomePage/BlogHomePage'));
const SingleBlogPost = lazy(() => import('./pages/SingleBlogPost/SingleBlogPost'));
const ViewHeroCard = lazy(() => import('./pages/ViewHeroCad/ViewHeroCard'));
const MyHeroCardWishList = lazy(() => import('./pages/MyHeroCardWishList/MyHeroCardWishList'));
const TradingCardFantasyGame = lazy(() => import('./pages/TradingCardFantasyGame/TradingCardFantasyGame'));
const CollectorRewards = lazy(() => import('./pages/CollectorRewards/CollectorRewards'));
const AdminLogin = lazy(() => import('./pages/AdminLogin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'));
const AdminOverview = lazy(() => import('./pages/Admin/AdminOverview'));
const AdminMarketplace = lazy(() => import('./pages/Admin/AdminMarketplace'));
const AdminAuctions = lazy(() => import('./pages/Admin/AdminAuctions'));
const AdminUsers = lazy(() => import('./pages/Admin/AdminUsers'));

function BrowseSessionSync() {
    const { pathname } = useLocation();
    useEffect(() => {
        syncBrowseSessionToPathname(pathname);
    }, [pathname]);
    return null;
}

function RouteFallback() {
    const { pathname } = useLocation();
    const isAdminRoute = pathname.startsWith('/admin');
    return <PageLoader variant={isAdminRoute ? 'dark' : 'light'} />;
}

const racingFont = '"WDXL Lubrifont TC", "Trebuchet MS", sans-serif';

const theme = createTheme({
    typography: {
        fontFamily: racingFont,
        button: {
            fontFamily: racingFont,
            letterSpacing: '0.06em',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: '#283848',
                    fontFamily: racingFont,
                },
            },
        },
    },
});

function App() {
    useEffect(() => {
        // Initialize StatusBar configuration and detect Capacitor
        const initializeStatusBar = async () => {
            let isCapacitor = false;
            
            try {
                const { StatusBar, Style } = await import('@capacitor/status-bar');
                
                // Ensure status bar doesn't overlay webview content
                await StatusBar.setOverlaysWebView({ overlay: false });
                
                // Set status bar style and background
                await StatusBar.setStyle({ style: Style.Dark });
                await StatusBar.setBackgroundColor({ color: '#000000' });
                
                // Show status bar
                await StatusBar.show();
                
                // Detect if we're running in Capacitor
                try {
                    const { Capacitor } = await import('@capacitor/core');
                    isCapacitor = Capacitor.isNativePlatform();
                } catch (error) {
                    console.log('Capacitor not available:', error);
                }
            } catch (error) {
                console.log('StatusBar plugin not available:', error);
            }
            
            return isCapacitor;
        };
        
        // Initialize keyboard resize mode
        const initializeKeyboard = async () => {
            try {
                const { Keyboard } = await import('@capacitor/keyboard');
                await Keyboard.setResizeMode({ mode: 'native' }); // maps to adjustResize on Android
            } catch (e) {
                console.error('Keyboard.setResizeMode error', e);
            }
        };

        // Initialize and get Capacitor status
        let removeHeaderListeners = () => {};

        initializeStatusBar().then(isCapacitor => {
            // Initialize keyboard
            initializeKeyboard();
            
            // Set header position based on platform
            const setHeaderPosition = () => {
                const header = document.querySelector('.menu-bar');
                const app = document.querySelector('.App');
                const homePageLogo = document.querySelector('.HomePage-Logorow');

                const applyAppOffset = (offsetPx) => {
                    const rounded = Math.max(0, Math.round(offsetPx));
                    if (app) {
                        app.style.paddingTop = `${rounded}px`;
                    }
                    document.documentElement.style.setProperty('--msv-app-header-offset', `${rounded}px`);
                };
                
                // Check if this is an Android tablet
                const isTablet = isAndroidTablet();
                
                // Remove any existing tablet classes
                header?.classList.remove('android-tablet');
                app?.classList.remove('android-tablet');
                homePageLogo?.classList.remove('android-tablet');
                
                if (window.innerWidth <= 768) {
                    if (isCapacitor) {
                        // CAPACITOR ANDROID APP - Special positioning
                        const statusBarHeight = window.statusBarHeight || 24;
                        if (header) {
                            header.style.top = `${statusBarHeight}px`;
                            header.style.position = 'fixed';
                            header.style.zIndex = '1000';
                        }
                        const headerH = header?.offsetHeight || 60;
                        applyAppOffset(statusBarHeight + headerH);
                        if (homePageLogo) {
                            homePageLogo.style.marginTop = '0';
                            homePageLogo.style.paddingTop = '0';
                        }
                    } else {
                        // MOBILE BROWSER - Original styling (no special positioning)
                        if (header) {
                            header.style.top = '0';
                            header.style.position = 'fixed';
                            header.style.zIndex = '1000';
                        }
                        applyAppOffset(header?.offsetHeight || 60);
                        if (homePageLogo) {
                            homePageLogo.style.marginTop = '0';
                            homePageLogo.style.paddingTop = '0';
                        }
                    }
                } else {
                    // TABLET/DESKTOP - Check if it's an Android tablet
                    if (isTablet) {
                        // ANDROID TABLET - Position header at top with grey background extending to status bar
                        if (header) {
                            header.classList.add('android-tablet');
                            header.style.top = '0'; // Position header at very top - grey background extends to status bar
                            header.style.position = 'fixed';
                            header.style.zIndex = '1000';
                            header.style.backgroundColor = '#283848';
                            header.style.backgroundImage =
                                'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 42%), linear-gradient(115deg, #1c2834 0%, #283848 38%, #334a5c 72%, #283848 100%)';
                            header.style.borderBottom = '1px solid rgba(255, 255, 255, 0.12)';
                            header.style.boxShadow = '0px 4px 6px rgba(0, 0, 0, 0.1)';
                            header.style.paddingTop = '2.5rem'; // Add extra padding to push header content down (40px)
                            header.style.alignItems = 'flex-start'; // Align items to the top instead of center
                            
                            // Fix hamburger menu positioning specifically
                            const hamburgerMenu = header.querySelector('.menu-toggle');
                            if (hamburgerMenu) {
                                hamburgerMenu.style.top = '2.5rem'; // Align hamburger menu to same top level as logo
                                hamburgerMenu.style.position = 'absolute';
                            }
                        }
                        if (app) {
                            app.classList.add('android-tablet');
                        }
                        applyAppOffset(header?.offsetHeight || 100);
                        if (homePageLogo) {
                            homePageLogo.classList.add('android-tablet');
                            homePageLogo.style.marginTop = '0';
                            homePageLogo.style.paddingTop = '0';
                        }
                    } else {
                        // DESKTOP - Original styling (no special positioning)
                        if (header) {
                            header.style.top = '0';
                            header.style.position = 'fixed';
                            header.style.zIndex = '1000';
                        }
                        applyAppOffset(header?.offsetHeight || 60);
                        if (homePageLogo) {
                            homePageLogo.style.marginTop = '0';
                            homePageLogo.style.paddingTop = '0';
                        }
                    }
                }

                // Re-measure after layout so content sits flush under the fixed header
                if (header) {
                    const statusBarPad =
                        window.innerWidth <= 768 && isCapacitor
                            ? (window.statusBarHeight || 24)
                            : 0;
                    applyAppOffset(statusBarPad + header.getBoundingClientRect().height);
                }
            };
            
            setHeaderPosition();
            window.addEventListener('resize', setHeaderPosition);

            const header = document.querySelector('.menu-bar');
            let resizeObserver;
            if (header && typeof ResizeObserver !== 'undefined') {
                // Login/logout changes header contents (messages, profile) and can desync padding.
                resizeObserver = new ResizeObserver(() => setHeaderPosition());
                resizeObserver.observe(header);
            }

            removeHeaderListeners = () => {
                window.removeEventListener('resize', setHeaderPosition);
                resizeObserver?.disconnect();
            };
        });
        
        // Prevent zooming and horizontal scrolling on Android (mobile only)
        const preventZoomAndScroll = () => {
            // Only apply on mobile devices
            if (window.innerWidth <= 768) {
                // Prevent pinch-to-zoom
                document.addEventListener('gesturestart', (e) => e.preventDefault());
                document.addEventListener('gesturechange', (e) => e.preventDefault());
                document.addEventListener('gestureend', (e) => e.preventDefault());
                
                // Prevent double-tap to zoom
                let lastTouchEnd = 0;
                document.addEventListener('touchend', (e) => {
                    const now = (new Date()).getTime();
                    if (now - lastTouchEnd <= 300) {
                        e.preventDefault();
                    }
                    lastTouchEnd = now;
                }, false);
                
                // Prevent horizontal scrolling
                document.addEventListener('touchmove', (e) => {
                    if (e.touches.length > 1) {
                        e.preventDefault();
                    }
                }, { passive: false });
                
                // Prevent horizontal swipe gestures
                document.addEventListener('touchmove', (e) => {
                    const touch = e.touches[0];
                    const startX = touch.clientX;
                    
                    // If horizontal movement is detected, prevent it
                    if (Math.abs(touch.clientX - startX) > 10) {
                        e.preventDefault();
                    }
                }, { passive: false });
                
                // Prevent horizontal scrolling on all elements
                const preventHorizontalScroll = (e) => {
                    if (e.deltaX !== 0) {
                        e.preventDefault();
                    }
                };
                
                document.addEventListener('wheel', preventHorizontalScroll, { passive: false });
            }
        };
        
        preventZoomAndScroll();
        scheduleIdlePrefetch();

        return () => {
            removeHeaderListeners();
        };
    }, []);

    const runningNativeAndroid = isAndroidCapacitor();

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <NotificationProvider>
                <UserProvider>
                    <WishlistProvider>
                    <Router>
                    <BrowseSessionSync />
                    <MessagesProvider>
                    <NotificationsProvider>
                    <div className="App">
                        <AdminSessionGate />
                        <Header />
                        <MessagesDrawer />

                        {/* Mount ONCE so it persists across route changes */}
                        {runningNativeAndroid && <AdMobBannerManager />}

                        <Suspense fallback={<RouteFallback />}>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/signup" element={<SignupPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/profilepage" element={<ProfilePage />} />
                            <Route path="/auctions" element={<CollectorRewards />} />
                            <Route path="/collectorrewards" element={<Navigate to="/auctions" replace />} />
                            <Route path="/resetpassword" element={<SubmitResetPassword />} />
                            <Route path="/resendEmailVerification" element={<ResendEmailVerification />} />
                            <Route path="/reset-password" element={<NewPasswordReset />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/additems" element={<AddCollectible />} />
                            <Route path="/addcollectible" element={<Navigate to="/additems?type=collectible" replace />} />
                            <Route path="/addDiecast" element={<Navigate to="/additems?type=diecast" replace />} />
                            <Route path="/addHeroCard" element={<Navigate to="/additems?type=heroCard" replace />} />
                            <Route path="/searchcollectibles" element={<SearchCollectibles />} />
                            <Route path="/searchDiecasts" element={<SearchCollectibles vaultMode="diecasts" />} />
                            <Route path="/collectionItem/:collectionItemId" element={<ViewCollectionItem />} />
                            <Route path="/collectionItem/:collectionItemId/edit" element={<AddCollectible />} />
                            <Route path="/marketplace" element={<Marketplace />} />
                            <Route path="/myitems" element={<MyItems />} />
                            <Route path="/mycollection" element={<Navigate to="/myitems?type=collectibles&page=1" replace />} />
                            <Route path="/createmarketplaceitem" element={<CreateMarketplaceItem />} />
                            <Route path="/marketplaceItem/:marketplaceItemId" element={<ViewMarketplaceItem />} />
                            <Route path="/mymarketplacelistings" element={<MyMarketplaceListings />} />
                            <Route path="/mymarketplacewishlist" element={<Navigate to="/myHeroCardWishList?type=marketplace" replace />} />
                            <Route path="/searchUsersCollection/:userId" element={<SearchUsersItems />} />
                            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                            <Route path="/blog" element={<BlogHomePage />} />
                            <Route path="/blog/:id" element={<SingleBlogPost />} />

                            <Route path="/searchHeroCards" element={<SearchHeroCards />} />
                            <Route path="/myHeroCards" element={<Navigate to="/myitems?type=heroCards&page=1" replace />} />
                            <Route path="/myHeroCardWishList" element={<MyHeroCardWishList />} />
                            <Route path="/herocard/:heroCardId" element={<ViewHeroCard />} />

                            <Route path="/nascardFantasy" element={<TradingCardFantasyGame />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<Navigate to="overview" replace />} />
                                <Route path="overview" element={<AdminOverview />} />
                                <Route path="marketplace" element={<AdminMarketplace />} />
                                <Route path="auctions" element={<AdminAuctions />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="dashboard" element={<Navigate to="/admin/overview" replace />} />
                            </Route>
                        </Routes>
                        </Suspense>
                    </div>
                    </NotificationsProvider>
                    </MessagesProvider>
                    </Router>
                    </WishlistProvider>
                </UserProvider>
            </NotificationProvider>
        </ThemeProvider>
    );
}

export default App;